import React, { useState, useEffect } from "react";
import { Upload, X, Music } from "lucide-react";
import { toast } from "react-toastify";
import Loading from "../../../common/HousGlass Loader/Loading";
import { useNavigate, useParams } from "react-router-dom";
import { useCategories } from "../../../hooks/categories/useCategories";
import { useAudio, useUpdateAudio } from "../../../hooks/admin/useAdminAudios";
import styles from "./AddAudios.module.css";

const UpdateAudio = () => {
  const { id } = useParams();
  const { audio, isLoading: isLoadingAudio } = useAudio(id);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    mobileImage: null,
    computerImage: null,
    audioFile: null,
    genreId: "",
    genreName: "", // Ensure this field is initialized 
    language: "english",
    duration: 0,
    isPublic: false,
    meta_description: "",
    meta_keywords: "",
    page_title: "",
    slug: "",
  });

  const [mobileImagePreview, setMobileImagePreview] = useState("");
  const [computerImagePreview, setComputerImagePreview] = useState("");
  const [audioFileName, setAudioFileName] = useState("");
  const [existingMobileImage, setExistingMobileImage] = useState(null);
  const [existingComputerImage, setExistingComputerImage] = useState(null);
  const [existingAudioFile, setExistingAudioFile] = useState(null);

  const { categories = [] } = useCategories("Audio");
  const { updateAudio, isLoading } = useUpdateAudio();
  const navigate = useNavigate();

  useEffect(() => {
    if (audio) {
      const selectedGenre = categories.find(genre => genre.id === audio.genre_id);

      setFormData({
        title: audio.title || "",
        description: audio.description || "",
        mobileImage: null,
        computerImage: null,
        audioFile: null,
        genreId: audio.genre_id || "",
        // Use the existing genre_name from audio or get it from the found genre
        genreName: audio.genre_name || (selectedGenre ? selectedGenre.name : ""),
        language: audio.language || "english",
        duration: audio.duration || 0,
        isPublic: audio.is_public || false,
        meta_description: audio.meta_description || "",
        meta_keywords: audio.meta_keywords || "",
        page_title: audio.page_title || "",
        slug: audio.slug || "",
      });

      // Handle mobile image
      if (audio.mobile_image_url) {
        setExistingMobileImage(audio.mobile_image_url);
        setMobileImagePreview(audio.mobile_image_url);
      }

      // Handle computer image
      if (audio.computer_image_url) {
        setExistingComputerImage(audio.computer_image_url);
        setComputerImagePreview(audio.computer_image_url);
      }

      // Handle audio file
      if (audio.audio_url) {
        setExistingAudioFile(audio.audio_url);
        // Extract filename from URL
        const audioFileName =
          audio.audio_url.split("/").pop() || audio.title + " (Current Audio)";
        setAudioFileName(audioFileName);
      }
    }
  }, [audio,categories]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'genreId') {
      // Find the selected genre to get its name
      const selectedGenre = categories.find(genre => genre.id === value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        genreName: selectedGenre ? selectedGenre.name : ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === "mobile") {
          setMobileImagePreview(reader.result);
          setFormData((prev) => ({ ...prev, mobileImage: file }));
          setExistingMobileImage(null);
        } else {
          setComputerImagePreview(reader.result);
          setFormData((prev) => ({ ...prev, computerImage: file }));
          setExistingComputerImage(null);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAudioUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error("Audio file size should be less than 100MB");
        return;
      }
      setAudioFileName(file.name);
      setFormData((prev) => ({ ...prev, audioFile: file }));
      setExistingAudioFile(null);

      // Get audio duration
      const audio = new Audio();
      audio.src = URL.createObjectURL(file);
      audio.onloadedmetadata = () => {
        setFormData((prev) => ({
          ...prev,
          duration: Math.round(audio.duration),
        }));
      };
    }
  };

  const removeImage = (type) => {
    if (type === "mobile") {
      setMobileImagePreview("");
      setFormData((prev) => ({ ...prev, mobileImage: null }));
      setExistingMobileImage(null);
    } else {
      setComputerImagePreview("");
      setFormData((prev) => ({ ...prev, computerImage: null }));
      setExistingComputerImage(null);
    }
  };

  const removeAudio = () => {
    setAudioFileName("");
    setFormData((prev) => ({ ...prev, audioFile: null }));
    setExistingAudioFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!id) {
      toast.error("Audio ID is missing");
      return;
    }

    // Create FormData object for file uploads
    const formDataToSend = new FormData();

    // Add text fields
    formDataToSend.append("title", formData.title);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("genreId", formData.genreId);
    formDataToSend.append("genreName", formData.genreName); // Explicitly add genreName
    formDataToSend.append("language", formData.language);
    formDataToSend.append("duration", formData.duration);
    formDataToSend.append("isPublic", formData.isPublic);
    formDataToSend.append("meta_description", formData.meta_description);
    formDataToSend.append("meta_keywords", formData.meta_keywords);
    formDataToSend.append("page_title", formData.page_title);
    formDataToSend.append("slug", formData.slug);

    // Correctly handle image removals
    formDataToSend.append(
      "removeMobileImage",
      !existingMobileImage && !formData.mobileImage ? "true" : "false"
    );
    formDataToSend.append(
      "removeComputerImage",
      !existingComputerImage && !formData.computerImage ? "true" : "false"
    );

    // Add file fields only if they exist
    if (formData.mobileImage) {
      formDataToSend.append("mobileImage", formData.mobileImage);
    }

    if (formData.computerImage) {
      formDataToSend.append("computerImage", formData.computerImage);
    }

    if (formData.audioFile) {
      formDataToSend.append("audioFile", formData.audioFile);
    }

    try {
      // Pass the id separately from the formData
      await updateAudio({ id, formData: formDataToSend });
      toast.success("Audio updated successfully!");
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Error updating audio:", error);
      toast.error(error?.message || "Failed to update audio");
    }
  };

  if (isLoadingAudio || isLoading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  if (!audio) {
    return <div>Error: Audio not found</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Music className={styles.headerIcon} />
        <h1>Update Audio</h1>
      </div>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="slug">Slug</label>
          <input
            type="text"
            id="slug"
            name="slug"
            value={formData.slug}
            onChange={handleInputChange}
            placeholder="Auto-generated if left blank"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="page_title">Page Title</label>
          <input
            type="text"
            id="page_title"
            name="page_title"
            value={formData.page_title}
            onChange={handleInputChange}
            placeholder="SEO Page Title"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="meta_description">Meta Description</label>
          <textarea
            id="meta_description"
            name="meta_description"
            value={formData.meta_description}
            onChange={handleInputChange}
            rows="2"
            placeholder="SEO Meta Description"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="meta_keywords">Meta Keywords</label>
          <input
            type="text"
            id="meta_keywords"
            name="meta_keywords"
            value={formData.meta_keywords}
            onChange={handleInputChange}
            placeholder="Comma-separated keywords"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="genreId">Genre</label>
          <select
            id="genreId"
            name="genreId"
            value={formData.genreId}
            onChange={handleInputChange}
            required
            className={styles.select}
          >
            <option value="">Select a genre</option>
            {categories.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="language">Language</label>
          <select
            id="language"
            name="language"
            value={formData.language}
            onChange={handleInputChange}
            required
            className={styles.select}
          >
            <option value="english">English</option>
            <option value="hindi">Hindi</option>
            <option value="spanish">Spanish</option>
            <option value="french">French</option>
            <option value="german">German</option>
            <option value="chinese">Chinese</option>
            <option value="japanese">Japanese</option>
            <option value="korean">Korean</option>
            <option value="arabic">Arabic</option>
          </select>
        </div>

        <div className={styles.imageUploadSection}>
          <div className={styles.imageUpload}>
            <label>Mobile Cover Image</label>
            <div className={styles.uploadArea}>
              {mobileImagePreview ? (
                <div className={styles.imagePreview}>
                  <img
                    src={
                      formData.mobileImage
                        ? mobileImagePreview
                        : existingMobileImage
                    }
                    alt="Mobile cover preview"
                    onError={(e) => {
                      console.error(
                        "Failed to load mobile image:",
                        existingMobileImage
                      );
                      e.target.src = "/placeholder-image.png"; // Fallback to placeholder
                      e.target.onerror = null; // Prevent infinite error loop
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage("mobile")}
                    className={styles.removeButton}
                  >
                    <X />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <input
                    type="file"
                    id="mobileImage"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "mobile")}
                    className={styles.fileInput}
                  />
                  <Upload />
                  <span>Upload Mobile Cover</span>
                </div>
              )}
            </div>
          </div>

          <div className={styles.imageUpload}>
            <label>Computer Cover Image</label>
            <div className={styles.uploadArea}>
              {computerImagePreview ? (
                <div className={styles.imagePreview}>
                  <img
                    src={
                      formData.computerImage
                        ? computerImagePreview
                        : existingComputerImage
                    }
                    alt="Computer cover preview"
                    onError={(e) => {
                      console.error(
                        "Failed to load computer image:",
                        existingComputerImage
                      );
                      e.target.src = "/placeholder-image.png"; // Fallback to placeholder
                      e.target.onerror = null; // Prevent infinite error loop
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage("computer")}
                    className={styles.removeButton}
                  >
                    <X />
                  </button>
                </div>
              ) : (
                <div className={styles.uploadPlaceholder}>
                  <input
                    type="file"
                    id="computerImage"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, "computer")}
                    className={styles.fileInput}
                  />
                  <Upload />
                  <span>Upload Computer Cover</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={styles.audioUpload}>
          <label>Audio File</label>
          <div className={styles.uploadArea}>
            {audioFileName ? (
              <div className={styles.audioPreview}>
                <span>{audioFileName}</span>
                <button
                  type="button"
                  onClick={removeAudio}
                  className={styles.removeButton}
                >
                  <X />
                </button>
              </div>
            ) : (
              <div className={styles.uploadPlaceholder}>
                <input
                  type="file"
                  id="audioFile"
                  accept="audio/*"
                  onChange={handleAudioUpload}
                  className={styles.fileInput}
                />
                <Upload />
                <span>Upload Audio File</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="isPublic"
              checked={formData.isPublic}
              onChange={handleInputChange}
            />
            Make this audio public
          </label>
        </div>

        <button type="submit" className={styles.submitButton}>
          Update Audio
        </button>
      </form>
    </div>
  );
};

export default UpdateAudio;
